import { useEffect } from 'react';

function CloseIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function LockIcon() {
  return (
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-base" aria-hidden>
      🔒
    </span>
  );
}

function InfoIcon({ children }) {
  return (
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-base" aria-hidden>
      {children}
    </span>
  );
}

function SectionHeading({ children }) {
  return <h3 className="text-sm font-semibold text-[#4DA3FF] sm:text-base">{children}</h3>;
}

function FeatureRow({ label }) {
  return (
    <li className="flex items-center gap-3 text-sm text-white/85 sm:text-[15px]">
      <LockIcon />
      <span>{label}</span>
    </li>
  );
}

function InfoRow({ icon, label }) {
  return (
    <p className="flex items-center gap-3 text-sm text-white/85 sm:text-[15px]">
      <InfoIcon>{icon}</InfoIcon>
      <span>{label}</span>
    </p>
  );
}

export default function SubscriptionDetailsModal({ open, plan, onClose, onDownloadInvoice }) {
  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose?.();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open || !plan) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscription-details-title"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-[560px] overflow-y-auto rounded-[28px] bg-[#2A2A2A] px-5 py-6 shadow-2xl sm:px-7 sm:py-7"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-[#3A3A3A] text-white transition hover:bg-[#454545] sm:right-5 sm:top-5"
        >
          <CloseIcon />
        </button>

        <div className="flex flex-wrap items-center gap-3 pr-12">
          <h2
            id="subscription-details-title"
            className="text-lg font-semibold leading-snug text-white sm:text-[22px]"
          >
            {plan.title}
          </h2>
          <span
            className={`rounded-full px-4 py-1 text-sm font-semibold text-white ${
              plan.active ? 'bg-[#69991D]' : 'bg-[#40330F]'
            }`}
          >
            {plan.active ? 'Active' : 'Expired'}
          </span>
        </div>

        <div className="mt-6 space-y-6">
          <section>
            <SectionHeading>{plan.includedHeading}</SectionHeading>
            <ul className="mt-3 space-y-2.5">
              {plan.features.map((feature) => (
                <FeatureRow key={feature} label={feature} />
              ))}
            </ul>
          </section>

          <section>
            <SectionHeading>Class</SectionHeading>
            <div className="mt-3 space-y-2.5">
              <InfoRow icon="🎓" label={`Class : ${plan.classLabel}`} />
              {/* <InfoRow icon="🚀" label={`Stream: ${plan.stream}`} />
              <InfoRow icon="📚" label={`Subject Active : ${plan.subjects}`} /> */}
            </div>
          </section>

          <section>
            <SectionHeading>Payment Info</SectionHeading>
            <div className="mt-3 space-y-2.5">
              <InfoRow icon="💳" label={`Payment: ${plan.paymentMethod}`} />
              <InfoRow icon="📅" label={`Last Payment on: ${plan.lastPaymentDate}`} />
            </div>
          </section>
        </div>

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => onDownloadInvoice?.(plan)}
            className="inline-flex items-center gap-2 rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-[#1B1B1B] transition hover:bg-[#F3F3F3] sm:text-base"
          >
            <span className="text-lg text-[#1A87CC]" aria-hidden>
              📄
            </span>
            Download Invoice
          </button>
        </div>
      </div>
    </div>
  );
}
